/**
 * Layout Page Prompt
 *
 * Helps create page layouts using Classic Theme components.
 */

import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';

const LayoutPageSchema = z.object({
  pageType: z.string().describe('Type of page (dashboard, public, auth, error, form)'),
  features: z
    .string()
    .optional()
    .describe('Comma-separated features needed (e.g., "sidebar, search, user-menu")'),
});

/**
 * Register the layout_page prompt
 */
export function registerLayoutPagePrompt(server: McpServer): void {
  server.prompt(
    'layout_page',
    'Create a page layout using Classic Theme layout components',
    LayoutPageSchema.shape,
    async (params) => {
      const parsed = LayoutPageSchema.parse(params);
      const pageType = parsed.pageType || 'public';
      const features = (parsed.features || '')
        .split(',')
        .map((f: string) => f.trim())
        .filter(Boolean);

      const featureList = features.length > 0 ? `With features: ${features.join(', ')}` : '';

      const layoutGuide: Record<string, string> = {
        dashboard: `DashboardLayout - For authenticated dashboard pages with sidebar navigation
- Props: navigation (NavSection[]), user (User), appName, pageTitle, sidebarVariant, headerSearch
- Pinning Props: enablePinning, pinnedSectionTitle, initialPinnedItems, onPinnedChange, pinIcon
- Supports: collapsible sidebar, user menu, quick links, mobile responsive
- Role-based Expansion: Use expandForRoles on NavSection to auto-expand sections for users with specific roles
- User Pinning: Enable pinnable items (pinnable: true on NavItem) with onPinnedChange callback for persistence`,

        public: `PublicLayout - For public-facing pages with header and footer
- Props: navigation (NavItem[]), footerLinks (NavSection[]), copyright, headerSearch
- Supports: responsive header nav, footer link sections, logo customization`,

        auth: `AuthLayout - For authentication pages (login, signup, etc.)
- Props: logoSubtitle, logoEnvironment, footerLinks, backgroundVariant, maxWidth
- Supports: centered card layout, decorative backgrounds, footer links`,

        error: `ErrorLayout - For error pages (404, 500, etc.)
- Props: statusCode, title, description, showHomeButton, showBackButton, illustration
- Supports: custom error illustrations, action buttons, status codes`,

        form: `FormPageLayout - For multi-step forms or data entry pages
- Props: title, description, helpText, showNotices
- Supports: optional sidebar, notices section, help text`,
      };

      const layoutInfo = layoutGuide[pageType] || layoutGuide.public;

      return {
        messages: [
          {
            role: 'user',
            content: {
              type: 'text',
              text: `Create a ${pageType} page layout. ${featureList}

Layout Component to Use:
${layoutInfo}

Requirements:
- Import from '@classic-homes/theme-svelte'
- Follow Svelte 5 runes syntax
- Use TypeScript for type safety
- Import and use proper types (NavSection, NavItem, User, etc.)

Key Types:
\`\`\`typescript
interface NavItem {
  id: string;
  name: string;
  href?: string;
  icon?: string;
  badge?: string | number;
  children?: NavItem[];
  active?: boolean;
  pinnable?: boolean;  // Allow this item to be pinned by users
}

interface NavSection {
  id: string;
  title?: string;
  items: NavItem[];
  collapsible?: boolean;
  expanded?: boolean;
  expandForRoles?: string[];  // Auto-expand for users with these roles
}

interface User {
  id: string;
  name: string;
  email?: string;
  avatar?: string;
  roles?: string[];  // Used for role-based section expansion
}

// For external persistence of sidebar preferences
interface SidebarPreferences {
  pinnedItems: string[];
  expandedSections: string[];
  expandedItems: string[];
}
\`\`\`

Example for ${pageType}Layout:
\`\`\`svelte
<script lang="ts">
  import { ${pageType.charAt(0).toUpperCase() + pageType.slice(1)}Layout } from '@classic-homes/theme-svelte';
  import type { NavSection, NavItem, User } from '@classic-homes/theme-svelte';

  // Define navigation, user data, etc.
</script>

<${pageType.charAt(0).toUpperCase() + pageType.slice(1)}Layout
  {/* props */}
>
  <!-- Page content here -->
</${pageType.charAt(0).toUpperCase() + pageType.slice(1)}Layout>
\`\`\``,
            },
          },
        ],
      };
    }
  );
}
